import type {
  Amount,
  ContractRouteRef,
  OrderContractElement,
  OrderContractPriceBasis,
  OrderPricingStrategy,
} from '@hero/schema';

/** Epsilon for comparing load price to frozen route quote (matches price-edit drawer). */
export const PRICE_BASIS_EPSILON = 0.001;

export type PriceBasisInput = Pick<
  OrderContractElement,
  'route' | 'amount' | 'pricingStrategy'
> & {
  priceBasis?: OrderContractElement['priceBasis'];
};

/**
 * Whether the load price matches the frozen route snapshot quote.
 */
export function loadPriceMatchesRouteQuote(
  itemsOriginalSubtotal: number,
  routePrice: number,
  epsilon = PRICE_BASIS_EPSILON,
): boolean {
  return Math.abs(itemsOriginalSubtotal - routePrice) <= epsilon;
}

/**
 * Derive {@link OrderContractPriceBasis} from a leg's current commercial
 * state. Used on every create/update write and by the backfill migration.
 */
export function computePriceBasis(leg: PriceBasisInput): OrderContractPriceBasis {
  const routePrice = leg.route?.price;
  const loadPrice = leg.amount?.itemsOriginalSubtotal ?? 0;

  if (routePrice !== undefined && loadPriceMatchesRouteQuote(loadPrice, routePrice)) {
    return 'route_quoted';
  }

  return 'manual';
}

/**
 * Legacy fallback for rows written before `priceBasis` was persisted.
 * Mirrors the migration backfill rules.
 */
export function computePriceBasisLegacy(leg: PriceBasisInput): OrderContractPriceBasis {
  const strategy: OrderPricingStrategy | undefined = leg.pricingStrategy;

  if (strategy === 'one_time_pricing' || strategy === 'admin_custom_pricing') {
    return 'manual';
  }

  if (strategy === 'unresolved_pricing' || strategy === 'distance_based_pricing') {
    const loadPrice = leg.amount?.itemsOriginalSubtotal ?? 0;
    if (loadPrice > 0) return 'manual';
    return 'manual';
  }

  return computePriceBasis(leg);
}

export function buildPriceAuditPayload(input: {
  newAmount: Amount;
  prevAmount?: Amount;
  routeRef?: ContractRouteRef | null;
}) {
  const { newAmount, prevAmount, routeRef } = input;
  return {
    ...(prevAmount !== undefined ? { prevAmount } : {}),
    newAmount,
    ...(routeRef?.routeId !== undefined ? { routeId: routeRef.routeId } : {}),
    ...(routeRef?.price !== undefined ? { quotedRoutePrice: routeRef.price } : {}),
  };
}

export function priceTimelineEventForBasis(
  priceBasis: OrderContractPriceBasis,
): 'contract_price_quoted' | 'contract_price_set_manual' {
  return priceBasis === 'route_quoted' ? 'contract_price_quoted' : 'contract_price_set_manual';
}
